import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import Button from 'antd/es/button';
import DatePicker from 'antd/es/date-picker';
import Form from 'antd/es/form';
import type { FormInstance } from 'antd/es/form';
import Input from 'antd/es/input';
import InputNumber from 'antd/es/input-number';
import Select from 'antd/es/select';
import Switch from 'antd/es/switch';
import Typography from 'antd/es/typography';
import React from 'react';
import styled from 'styled-components';

import type { JSONSchemaType } from '@mai/types';

import {
  getEnumOptions,
  getFieldDescription,
  getFieldTitle,
  getInputType,
  getValidationRules,
  hasEnumValues,
} from './utils';

import { logger } from '@utils/logger';

const { TextArea, Password } = Input;
const { Text } = Typography;

const ArrayContainer = styled.div`
  ${({ theme }) => `
  border: ${theme.lineWidth}px solid ${theme.colorBorder};
  border-radius: ${theme.borderRadius}px;
  padding: ${theme.padding}px;
  background: ${theme.colorBgContainer};
`}
`;

const ArrayItem = styled.div`
  ${({ theme }) => `
  display: flex;
  gap: ${theme.marginXS}px;
  margin-bottom: ${theme.marginSM}px;
  align-items: flex-start;

  &:last-child {
    margin-bottom: 0;
  }

  > :first-child {
    flex: 1;
  }
`}
`;

const FieldDescription = styled(Text)`
  ${({ theme }) => `
  display: block;
  margin-top: ${theme.marginXXS}px;
  font-size: ${theme.fontSizeSM}px;
  color: ${theme.colorTextSecondary};
`}
`;

const FullWidthInputNumber = styled(InputNumber)`
  width: 100%;
`;

const FullWidthDatePicker = styled(DatePicker)`
  width: 100%;
`;

const ArrayObjectItem = styled.div`
  ${({ theme }) => `
  margin-bottom: ${theme.margin}px;
`}
`;

const ArrayObjectHeader = styled.div`
  ${({ theme }) => `
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.marginXS}px;
`}
`;

const ArrayObjectContent = styled.div`
  ${({ theme }) => `
  padding-left: ${theme.padding}px;
`}
`;

const ZeroMarginFormItem = styled(Form.Item)`
  margin-bottom: 0 !important;
`;

interface FormFieldProps {
  schema: JSONSchemaType;
  name: string | string[];
  fieldName: string;
  required: boolean;
  disabled?: boolean;
  readonly?: boolean;
  form: FormInstance;
  onChange?: () => void;
  renderField: (
    schema: JSONSchemaType,
    fieldName: string,
    parentName: string | string[],
    parentRequired: string[],
  ) => React.ReactNode;
  layout?: 'horizontal' | 'vertical' | 'inline';
  labelCol?: { span: number };
  wrapperCol?: { span: number };
  customComponents?: Record<
    string,
    React.ComponentType<{
      schema: JSONSchemaType;
      disabled?: boolean;
      readonly?: boolean;
      value?: unknown;
      onChange?: (value: unknown) => void;
    }>
  >;
  showDescription?: boolean;
}

export const FormField: React.FC<FormFieldProps> = ({
  schema,
  name,
  fieldName,
  required,
  disabled = false,
  readonly = false,
  onChange,
  renderField,
  labelCol,
  wrapperCol,
  customComponents = {},
  showDescription = true,
}) => {
  const label = getFieldTitle(schema, fieldName);
  const description = getFieldDescription(schema);
  const inputType = getInputType(schema);
  const rules = getValidationRules(schema, required, fieldName);

  // Normalize the name prop for Form.Item
  // If it's an array with one element, use just the string
  const normalizedName =
    Array.isArray(name) && name.length === 1 ? name[0] : name;

  logger.debug(
    {
      name,
      normalizedName,
      fieldName,
      inputType,
      label,
      schema,
    },
    'FormField render',
  );

  // Check for custom component
  const customKey = schema.format || inputType;
  const CustomComponent = customComponents[customKey];
  if (CustomComponent) {
    return (
      <Form.Item
        name={normalizedName}
        label={label}
        rules={rules}
        labelCol={labelCol}
        wrapperCol={wrapperCol}
      >
        <CustomComponent
          schema={schema}
          disabled={disabled}
          readonly={readonly}
        />
      </Form.Item>
    );
  }

  // Handle different input types
  switch (inputType) {
    case 'select': {
      const options = getEnumOptions(schema);
      const isMultiple = schema.type === 'array';

      return (
        <Form.Item
          name={normalizedName}
          label={label}
          rules={rules}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          extra={showDescription && description ? description : undefined}
        >
          <Select
            placeholder={`Select ${label.toLowerCase()}`}
            disabled={disabled || readonly}
            mode={isMultiple ? 'multiple' : undefined}
            allowClear
            showSearch
            optionFilterProp="label"
          >
            {options.map((option) => (
              <Select.Option key={String(option.value)} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      );
    }

    case 'switch':
      return (
        <Form.Item
          name={normalizedName}
          label={label}
          rules={rules}
          valuePropName="checked"
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          extra={showDescription && description ? description : undefined}
        >
          <Switch disabled={disabled || readonly} />
        </Form.Item>
      );

    case 'number':
      return (
        <Form.Item
          name={normalizedName}
          label={label}
          rules={rules}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          extra={showDescription && description ? description : undefined}
        >
          <FullWidthInputNumber
            placeholder={description || `Enter ${label.toLowerCase()}`}
            disabled={disabled || readonly}
            min={schema.minimum}
            max={schema.maximum}
            step={schema.multipleOf}
            precision={schema.type === 'integer' ? 0 : undefined}
            onChange={(_value) => {
              if (onChange) onChange();
            }}
          />
        </Form.Item>
      );

    case 'date':
      return (
        <Form.Item
          name={normalizedName}
          label={label}
          rules={rules}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          extra={showDescription && description ? description : undefined}
        >
          <FullWidthDatePicker
            disabled={disabled || readonly}
            placeholder={description || `Select date`}
          />
        </Form.Item>
      );

    case 'datetime':
      return (
        <Form.Item
          name={normalizedName}
          label={label}
          rules={rules}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          extra={showDescription && description ? description : undefined}
        >
          <FullWidthDatePicker
            showTime
            disabled={disabled || readonly}
            placeholder={description || `Select date and time`}
          />
        </Form.Item>
      );

    case 'textarea':
      return (
        <Form.Item
          name={normalizedName}
          label={label}
          rules={rules}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          extra={showDescription && description ? description : undefined}
        >
          <TextArea
            rows={4}
            placeholder={description || `Enter ${label.toLowerCase()}`}
            disabled={disabled || readonly}
            maxLength={schema.maxLength}
            showCount={!!schema.maxLength}
          />
        </Form.Item>
      );

    case 'password':
      return (
        <Form.Item
          name={normalizedName}
          label={label}
          rules={rules}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          extra={showDescription && description ? description : undefined}
        >
          <Password
            placeholder={description || `Enter ${label.toLowerCase()}`}
            disabled={disabled || readonly}
          />
        </Form.Item>
      );

    case 'array': {
      // Determine item type from schema.items or default to string
      let itemType = 'string';
      let itemSchema: JSONSchemaType | undefined;

      if (
        schema.items &&
        typeof schema.items === 'object' &&
        !Array.isArray(schema.items)
      ) {
        itemSchema = schema.items as JSONSchemaType;
        const schemaType = itemSchema.type;
        itemType = Array.isArray(schemaType)
          ? schemaType[0]
          : schemaType || 'string';
      }

      // Handle enum arrays (like resource_ids)
      if (hasEnumValues(schema)) {
        return (
          <Form.Item
            name={normalizedName}
            label={label}
            rules={rules}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
          >
            <Select
              mode="multiple"
              placeholder={`Select ${label.toLowerCase()}`}
              disabled={disabled || readonly}
              allowClear
              showSearch
              optionFilterProp="label"
            >
              {getEnumOptions(schema).map((option) => (
                <Select.Option key={String(option.value)} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
            {showDescription && description && (
              <FieldDescription>{description}</FieldDescription>
            )}
          </Form.Item>
        );
      }

      // Handle arrays of primitives (string, number, integer)
      if (
        itemSchema === undefined ||
        itemType === 'string' ||
        itemType === 'number' ||
        itemType === 'integer'
      ) {
        // Check if array items have a custom format (dataset, file, etc.)
        const itemFormat = itemSchema?.format;
        const CustomArrayItemComponent = itemFormat
          ? customComponents[itemFormat]
          : null;

        return (
          <Form.Item
            label={label}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
            rules={rules}
          >
            <Form.List name={name}>
              {(fields, { add, remove }) => (
                <ArrayContainer>
                  {fields.map(({ key, name: itemName, ...restField }) => (
                    <ArrayItem key={key}>
                      <ZeroMarginFormItem
                        {...restField}
                        name={itemName}
                        rules={
                          itemSchema
                            ? getValidationRules(itemSchema, false, 'item')
                            : []
                        }
                      >
                        {CustomArrayItemComponent ? (
                          <CustomArrayItemComponent
                            schema={itemSchema as JSONSchemaType}
                            disabled={disabled}
                            readonly={readonly}
                          />
                        ) : itemType === 'string' ? (
                          <Input
                            placeholder={`Enter ${label.toLowerCase()} item`}
                            disabled={disabled || readonly}
                          />
                        ) : (
                          <FullWidthInputNumber
                            placeholder="Enter number"
                            disabled={disabled || readonly}
                          />
                        )}
                      </ZeroMarginFormItem>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(itemName)}
                        disabled={disabled || readonly}
                      />
                    </ArrayItem>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    disabled={disabled || readonly}
                  >
                    Add {label} Item
                  </Button>
                </ArrayContainer>
              )}
            </Form.List>
            {showDescription && description && (
              <FieldDescription>{description}</FieldDescription>
            )}
          </Form.Item>
        );
      }

      // Handle arrays of objects
      if (
        itemSchema &&
        itemType === 'object' &&
        itemSchema.properties &&
        Object.keys(itemSchema.properties).length > 0
      ) {
        return (
          <Form.Item label={label} labelCol={labelCol} wrapperCol={wrapperCol}>
            <Form.List name={name}>
              {(fields, { add, remove }) => (
                <ArrayContainer>
                  {fields.map(({ key, name: itemName }, index) => (
                    <ArrayObjectItem key={key}>
                      <ArrayObjectHeader>
                        <Text strong>
                          {label} #{index + 1}
                        </Text>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(itemName)}
                          disabled={disabled || readonly}
                        />
                      </ArrayObjectHeader>
                      <ArrayObjectContent>
                        {itemSchema.properties &&
                          Object.entries(itemSchema.properties).map(
                            ([propKey, propSchema]) =>
                              renderField(
                                propSchema as JSONSchemaType,
                                propKey,
                                [
                                  ...(Array.isArray(itemName)
                                    ? itemName
                                    : [itemName]),
                                ],
                                itemSchema.required || [],
                              ),
                          )}
                      </ArrayObjectContent>
                    </ArrayObjectItem>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    disabled={disabled || readonly}
                  >
                    Add {label} Item
                  </Button>
                </ArrayContainer>
              )}
            </Form.List>
            {showDescription && description && (
              <FieldDescription>{description}</FieldDescription>
            )}
          </Form.Item>
        );
      }

      // Fallback to JSON input for complex arrays
      return (
        <Form.Item
          name={normalizedName}
          label={label}
          rules={[
            ...rules,
            {
              validator: async (_, value) => {
                if (!value && !required) return;
                if (!value && required) throw new Error(`${label} is required`);
                try {
                  const parsed =
                    typeof value === 'string' ? JSON.parse(value) : value;
                  if (!Array.isArray(parsed)) {
                    throw new Error('Value must be an array');
                  }
                } catch {
                  throw new Error('Invalid JSON array');
                }
              },
            },
          ]}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          extra={showDescription && description ? description : undefined}
        >
          <TextArea
            rows={4}
            placeholder="Enter JSON array, e.g., [1, 2, 3]"
            disabled={disabled || readonly}
          />
        </Form.Item>
      );
    }

    case 'object':
      // Handle nested objects
      if (schema.properties && Object.keys(schema.properties).length > 0) {
        return (
          <Form.Item label={label} labelCol={labelCol} wrapperCol={wrapperCol}>
            <ArrayContainer>
              {Object.entries(schema.properties).map(([key, value]) =>
                renderField(
                  value as JSONSchemaType,
                  key,
                  name,
                  schema.required || [],
                ),
              )}
            </ArrayContainer>
            {showDescription && description && (
              <FieldDescription>{description}</FieldDescription>
            )}
          </Form.Item>
        );
      }

      // Fallback to JSON input
      return (
        <Form.Item
          name={normalizedName}
          label={label}
          rules={rules}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          extra={showDescription && description ? description : undefined}
        >
          <TextArea
            rows={4}
            placeholder="Enter JSON object"
            disabled={disabled || readonly}
          />
        </Form.Item>
      );

    default:
      // Text input
      logger.debug(
        {
          name,
          label,
          rules,
          nameType: typeof name,
          nameValue: name,
          isArray: Array.isArray(name),
          nameLength: Array.isArray(name) ? name.length : null,
        },
        'Rendering text input Form.Item',
      );

      return (
        <Form.Item
          name={normalizedName}
          label={label}
          rules={rules}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          extra={showDescription && description ? description : undefined}
        >
          <Input
            placeholder={description || `Enter ${label.toLowerCase()}`}
            disabled={disabled || readonly}
            maxLength={schema.maxLength}
            showCount={!!schema.maxLength}
          />
        </Form.Item>
      );
  }
};
